import { Stack } from '@mui/material';
import { RefObject } from 'react';

import { APPBAR_HEIGHT } from '@/components/architecture/Layout/AppBar/AppBar.constants';
import { useResizableRightPanel } from '@/hooks/useResizableRightPanel';
import { ChatSideContentPane } from '@/modules/common/Chat/ChatSideContent/ChatSideContentPane';
import { useChatSideContentContext } from '@/modules/common/Chat/ChatSideContent/context/chatSideContent.context';
import { SideDrawer } from '@/modules/common/SideDrawer';
import { THEME_SHADOWS } from '@/styles/themes/common';
import { COLORS } from '@/styles/tokens/colors';
import { OVERLAY_SM } from '@/styles/tokens/panelWidths';
import { zIndexes } from '@/styles/zIndexes';

import { useHouseholdChatContext } from '../contexts/householdChat.context';
import { HouseholdChatShell } from './HouseholdChatShell';
import { ResizeHandle } from './ResizeHandle';
import {
  SIDE_CONTENT_DEFAULT_WIDTH,
  SIDE_CONTENT_MIN_WIDTH,
} from './sideContentPanel.constants';

interface HouseholdChatOverlayProps {
  /**
   * The content area below the app bar. The side content panel's resize hook
   * measures it to translate drag positions into panel widths and to cap the
   * panel so the drawer never outgrows the viewport.
   */
  containerRef: RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  fullScreenModal?: boolean;
  modalHeaderHeight?: number;
}

/**
 * The overlay host: renders the chat shell in a SideDrawer, used below the
 * 1440px breakpoint where a docked sidebar would squeeze the page content
 * below its minimum usable width. The chat column is fixed-width; the side
 * content panel opens beside it inside the drawer — the drawer is already an
 * overlay, so the panel is one too. Only the panel resizes.
 */
export function HouseholdChatOverlay({
  containerRef,
  isOpen,
  fullScreenModal = false,
  modalHeaderHeight = 0,
}: HouseholdChatOverlayProps) {
  const { closeChat } = useHouseholdChatContext();
  const { sideContent } = useChatSideContentContext();
  const hasSideContent = Boolean(sideContent);

  const {
    handleMouseDown: handleSideContentResize,
    panelWidth: sideContentWidth,
    resizeRef: sideContentResizeRef,
  } = useResizableRightPanel({
    containerRef,
    defaultWidth: SIDE_CONTENT_DEFAULT_WIDTH,
    // No design cap (maxWidth): the viewport, via the container, is the
    // limiting factor in the drawer.
    minWidth: SIDE_CONTENT_MIN_WIDTH,
    // The panel's right edge is the chat column's left edge, not the
    // container's right edge: offset drag math by the chat column.
    rightOffset: OVERLAY_SM,
  });

  return (
    <SideDrawer
      // Sized by the columns inside: the fixed-width chat column, plus the
      // side content panel when content is open.
      width="auto"
      isOpen={isOpen}
      onClose={closeChat}
      hideBackdrop
      drawerPropsSx={{
        boxShadow: `${THEME_SHADOWS.lg}`,
        // The fixed app bar must stay visible while the chat is open, so the
        // drawer paper starts below it rather than covering the full
        // viewport height.
        top: fullScreenModal ? modalHeaderHeight : APPBAR_HEIGHT,
        height: fullScreenModal ? '100%' : `calc(100% - ${APPBAR_HEIGHT})`,
        ...(fullScreenModal &&
          modalHeaderHeight > 0 && {
            height: `calc(100% - ${modalHeaderHeight}px)`,
          }),
      }}
      modalProps={{
        // Keep the editor header accessible outside this chat host.
        container:
          fullScreenModal && modalHeaderHeight
            ? containerRef.current
            : undefined,
        // The panel stays open while the user navigates and works in the rest
        // of the app, so the drawer must not steal focus or lock page scroll.
        disableEnforceFocus: true,
        disableScrollLock: true,
        // Keep the drawer above the AppBar (MENU) but below full-screen
        // modals/trowsers (MODAL) so it doesn't overlap them when the
        // viewport narrows and the docked host switches to this overlay.
        sx: {
          zIndex: fullScreenModal ? zIndexes.MODAL + 1 : zIndexes.MENU + 1,
        },
      }}
      data-testid="household-chat-overlay"
    >
      <Stack direction="row" sx={{ height: '100%' }}>
        {hasSideContent && (
          <Stack
            sx={{
              flexShrink: 0,
              height: '100%',
              position: 'relative',
              width: `${sideContentWidth}px`,
            }}
          >
            <ResizeHandle
              onMouseDown={handleSideContentResize}
              resizeRef={sideContentResizeRef}
              // Laid over the panel's left edge (instead of taking flex
              // space) so the pane header and its bottom border span the
              // full panel width, like the chat column header beside it.
              sx={{
                bottom: 0,
                left: 0,
                position: 'absolute',
                top: 0,
                zIndex: 1,
              }}
            />
            <ChatSideContentPane />
          </Stack>
        )}
        {/* Children mode: the shell renders the chat header (with its own
            close/collapse control) instead of the drawer's built-in heading
            row and close button. */}
        <Stack
          sx={{
            flexShrink: 0,
            width: fullScreenModal
              ? `min(${OVERLAY_SM}px, 100vw)`
              : `${OVERLAY_SM}px`,
            // With the panel open, the chat column needs its own left
            // divider; the panel's left edge is the drawer's outer edge.
            ...(hasSideContent && {
              borderLeft: `solid 1px ${COLORS.GRAY[200]}`,
            }),
          }}
        >
          <HouseholdChatShell />
        </Stack>
      </Stack>
    </SideDrawer>
  );
}
